// src/pages/invoices/components/RetencionDialog.js
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  TextField, Button, IconButton, Box, Typography, Alert, Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Registrar la retención de IVA que un cliente le practicó a una factura.
 *
 * Aparece después de emitir, no al facturar: el cliente paga, retiene y recién
 * entonces manda el comprobante, a veces semanas más tarde.
 *
 * La retención **no baja el total de la factura**. La factura se emitió por su
 * monto completo y se cancela con dinero más el comprobante, que ante el SENIAT
 * vale como crédito. El diálogo lo dice en voz alta porque es la confusión más
 * cara: si alguien cree que la factura ahora vale menos, declara de menos.
 */

const campoOscuro = {
  '& .MuiOutlinedInput-root': {
    color: 'white',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
    '&.Mui-focused fieldset': { borderColor: '#F5A623' }
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#F5A623' },
  '& .MuiFormHelperText-root': { color: 'rgba(255,255,255,0.5)' }
};

/** Los dos porcentajes que fija la ley. Se ofrecen para no tipear el monto. */
const PORCENTAJES = [75, 100];

export const RetencionDialog = ({ factura, open, onClose, onGuardar, guardando }) => {
  const [monto, setMonto] = useState('');
  const [comprobante, setComprobante] = useState('');
  const [error, setError] = useState(null);

  const iva = Number(factura?.tax) || 0;
  const moneda = factura?.moneda || 'VES';
  const yaTiene = Number(factura?.ivaRetenido) > 0;

  useEffect(() => {
    if (!open) return;
    setMonto(factura?.ivaRetenido ? String(factura.ivaRetenido) : '');
    setComprobante(factura?.comprobanteRetencion || '');
    setError(null);
  }, [open, factura]);

  const montoNum = Number(monto) || 0;
  const porcentaje = iva > 0 && montoNum > 0 ? (montoNum / iva) * 100 : 0;
  const excede = montoNum > iva + 0.005;

  const aplicarPorcentaje = (p) => {
    setMonto((iva * p / 100).toFixed(2));
  };

  const guardar = async () => {
    setError(null);
    if (montoNum > 0 && !comprobante.trim()) {
      setError('Falta el número de comprobante que entregó el cliente.');
      return;
    }
    if (excede) {
      setError(`No se puede retener más IVA del que cobró la factura (${iva.toFixed(2)}).`);
      return;
    }
    try {
      await onGuardar({ monto: montoNum, comprobante: comprobante.trim(), porcentaje: montoNum > 0 ? Number(porcentaje.toFixed(2)) : undefined });
    } catch (e) {
      setError(e?.message || 'No se pudo registrar la retención.');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !guardando && onClose()}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: {
        backgroundColor: '#1B1B1F', color: 'white', borderRadius: '8px',
        border: '1px solid rgba(255,255,255,0.1)'
      } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <span>Retención de IVA — {factura?.number || ''}</span>
        <IconButton onClick={onClose} sx={{ color: 'white' }} disabled={guardando} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <DialogContentText sx={{ color: 'rgba(255,255,255,0.7)', mb: 2, fontSize: '0.9rem' }}>
          Cuando el cliente es contribuyente especial no paga el IVA completo:
          retiene una parte y entrega un comprobante, que vale como crédito ante
          el SENIAT.
        </DialogContentText>

        <Alert severity="info" sx={{ mb: 3, bgcolor: 'rgba(245,166,35,0.1)', color: 'rgba(255,255,255,0.85)',
          border: '1px solid rgba(245,166,35,0.25)', '& .MuiAlert-icon': { color: '#F5A623' } }}>
          Esto <strong>no cambia el total de la factura</strong>. Se emitió por{' '}
          {moneda} {Number(factura?.total || 0).toFixed(2)} y sigue valiendo eso: se
          cancela con dinero más el comprobante.
        </Alert>

        <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            IVA de la factura: <strong style={{ color: 'white' }}>{moneda} {iva.toFixed(2)}</strong>
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {PORCENTAJES.map((p) => (
            <Chip
              key={p}
              label={`${p}%`}
              size="small"
              onClick={() => aplicarPorcentaje(p)}
              disabled={guardando || iva <= 0}
              sx={{ bgcolor: 'rgba(245,166,35,0.15)', color: '#F5A623', fontWeight: 600,
                '&:hover': { bgcolor: 'rgba(245,166,35,0.3)' } }}
            />
          ))}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            autoFocus
            label="Monto retenido"
            type="number"
            value={monto}
            onChange={(e) => setMonto(e.target.value)}
            disabled={guardando}
            error={excede}
            helperText={
              excede ? `Supera el IVA de la factura (${iva.toFixed(2)})`
                : (montoNum > 0 ? `Equivale al ${porcentaje.toFixed(2)}% del IVA` : 'Dejalo en blanco o en 0 para quitar la retención')
            }
            sx={{ ...campoOscuro, flex: '1 1 180px' }}
          />
          <TextField
            label="Nº de comprobante"
            value={comprobante}
            onChange={(e) => setComprobante(e.target.value)}
            disabled={guardando}
            placeholder="20260800012345"
            helperText="El que emitió el cliente"
            sx={{ ...campoOscuro, flex: '1 1 200px' }}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2, bgcolor: 'rgba(211,47,47,0.12)', color: '#ef9a9a',
            border: '1px solid rgba(211,47,47,0.3)' }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={guardando} sx={{ color: 'rgba(255,255,255,0.7)' }}>
          Cancelar
        </Button>
        <Button
          onClick={guardar}
          disabled={guardando || excede}
          variant="contained"
          sx={{ bgcolor: '#F5A623', color: '#1C1525', fontWeight: 600, '&:hover': { bgcolor: '#D9890D' } }}
        >
          {guardando ? 'Guardando…' : (montoNum > 0 ? 'Registrar retención' : (yaTiene ? 'Quitar retención' : 'Guardar'))}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RetencionDialog;
